import React, { useEffect, useState } from 'react';
import { TelerikReportViewer } from "@progress/telerik-react-report-viewer";
import { useBoolean } from "@fluentui/react-hooks";
import {
    CommandBar,
    DatePicker,
    ICommandBarItemProps,
    PanelType,
    defaultDatePickerStrings,
    FontSizes
} from '@fluentui/react';
import { PanelGenerico } from '../../../components/generic/PanelGenerico';
import IconHelper from '../../../utils/helpers/iconHelper';
import themeBase from '../../../utils/theme/themeBase';
import TokenService from '../../../services/Authentication/TokenService';
import { urlTelerik } from '../../../services/http-common';
import { formatDate_DDMMYY, formatDateStr_DDMMYY } from '../../../utils/helpers/dateHelpers';
import reporteServicio from '../../../services/reporteService';
import { IExportarReporte, IParametro } from '../../../Interfaces/base/IBase';
import COLORES from '../../../utils/theme/colores';

interface ITacosSeguridad {
    reportName: string;
    parametros?: Record<string, string | null>;
    formatos?: number;
}

export const TacosSeguridad = (props: ITacosSeguridad) => {

    const { reportName, formatos = 2 } = props;

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    const [fechaInicio, setFechaInicio] = useState<Date>(firstDay);
    const [fechaFin, setFechaFin] = useState<Date>(today);

    const [filtros, setFiltros] = useState({
        FechaInicio: fechaInicio.toISOString(),
        FechaFin: fechaFin.toISOString()
    });

    const [key, setKey] = useState(0);

    const [
        isOpenPanelFiltro,
        { setTrue: openPanelFiltro, setFalse: dismissPanelFiltro },
    ] = useBoolean(false);

    const token = TokenService.getToken();

    const transformarParametros = (parametros: Record<string, string | null>): IParametro[] => {
        return Object.entries(parametros).map(([key, value]) => ({
            Key: key,
            Value: value ? String(value) : null,
        }));
    };

    function descargarReporte(extension: string) {
        const reporteExportar: IExportarReporte = {
            report: reportName,
            Extension: extension,
            Parametros: transformarParametros(filtros)
        };

        reporteServicio.exportaReporte(reporteExportar)
            .then((response) => {
                const link = document.createElement('a');
                link.href = response.data;
                link.download = 'reporte';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch(() => {
                alert("Error al descargar el reporte");
            });
    }

    const aplicarFiltro = () => {
        setFiltros({
            FechaInicio: fechaInicio.toISOString(),
            FechaFin: fechaFin.toISOString()
        });
        setKey(prev => prev + 1);
        dismissPanelFiltro();
    };

    const limpiarFiltro = () => {
        setFechaInicio(firstDay);
        setFechaFin(today);
        setFiltros({
            FechaInicio: firstDay.toISOString(),
            FechaFin: today.toISOString()
        });
        setKey(prev => prev + 1);
        dismissPanelFiltro();
    };

    const recargarComponente = () => {
        setKey(prevKey => prevKey + 1);
    };

    const _items: ICommandBarItemProps[] = [
        {
            ...IconHelper.ICON_RELOAD,
            onClick: recargarComponente,
        }
    ];

    const _farItems: ICommandBarItemProps[] = [
        {
            ...IconHelper.ICON_PDF,
            onClick: () => descargarReporte("PDF"),
        },
        {
            ...IconHelper.ICON_EXCEL,
            onClick: () => descargarReporte("XLSX"),
        },
        {
            ...IconHelper.ICON_FILTER,
            onClick: openPanelFiltro,
        }
    ];

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const exportButton = document.querySelector<HTMLElement>("#trv-main-menu-export-command");
            if (exportButton) {
                exportButton.remove();
                observer.disconnect();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [key]);

    return (
        <>
            <div className="ms-Grid-col ms-sm12 ms-lg12">
                <CommandBar
                    items={_items}
                    farItems={_farItems}
                    styles={themeBase.commadStyles}
                />
            </div>

            <PanelGenerico
                HeaderTitlesText={{
                    title: "Filtros",
                }}
                enabledHeader
                isOpen={isOpenPanelFiltro}
                onDismiss={dismissPanelFiltro}
                panelType={PanelType.smallFixedFar}
                cancelar={limpiarFiltro}
                registrar={aplicarFiltro}
                textRegister='Filtrar'
                textCancel='Limpiar filtro'
            >
                <div>
                    <div style={{ fontSize: FontSizes.size10, color: '#626264' }}>
                        <div>
                            <span style={{ marginRight: '8px' }}>✓</span>
                            {`Fecha Inicio: ${formatDateStr_DDMMYY(filtros.FechaInicio)}`}
                        </div>

                        <div style={{ marginTop: '4px' }}>
                            <span style={{ marginRight: '8px' }}>✓</span>
                            {`Fecha Fin: ${formatDateStr_DDMMYY(filtros.FechaFin)}`}
                        </div>
                    </div>

                    <br />

                    <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg9">
                        <DatePicker
                            label="Fecha Inicio"
                            value={fechaInicio}
                            onSelectDate={(date) => { if (date) setFechaInicio(date); }}
                            formatDate={formatDate_DDMMYY}
                            strings={defaultDatePickerStrings}
                            maxDate={fechaFin}
                            borderless
                            style={{ borderBottom: `solid 1px ${COLORES.Bases.primary}` }}
                        />
                        <br />

                        <DatePicker
                            label="Fecha Fin"
                            value={fechaFin}
                            onSelectDate={(date) => { if (date) setFechaFin(date); }}
                            formatDate={formatDate_DDMMYY}
                            strings={defaultDatePickerStrings}
                            minDate={fechaInicio}
                            maxDate={new Date()}
                            borderless
                            style={{ borderBottom: `solid 1px ${COLORES.Bases.primary}` }}
                        />
                    </div>
                </div>
            </PanelGenerico>

            <div key={key} style={{ width: "100%", height: "auto" }}>
                <TelerikReportViewer
                    serviceUrl={`${urlTelerik}/reports`}
                    reportSource={{
                        report: reportName,
                        parameters: filtros
                    }}
                    viewerContainerStyle={themeBase.telerikStyleV2}
                    parametersAreaVisible={true}
                    viewMode="PRINT_PREVIEW"
                    scaleMode="FIT_PAGE_WIDTH"
                    scale={1.0}
                    enableAccessibility={false}
                    authenticationToken={token}
                    skipBlankPages={false}
                />
            </div>
        </>
    );
};